/* src/components/pages/Extraerf22rentaenr.jsx - MOdificado el 07-02-2025 */
/* ---------- scraping Async para formulario 22 renta enrolado ---------- */

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";

import {
  TextField,
  Box,
  Button,
  Stack,
  Typography,
  MenuItem,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";

import { RiDownload2Fill } from "react-icons/ri";

import api from "../../services/api.js";

import { useAuth } from "../../context/AuthContext";
import { useFeedback } from "../../context/FeedbackContext";

import { CustomToolbar } from "../../components/datagrid/CustomToolbar";

import { normalizeRut } from "../utils/rutUtils";
import { validarRut } from "../utils/rutValidator";
import { formatFecha } from "../utils/dateUtils";

const Extraerf22rentaenr = ({ modo }) => {
  const { user } = useAuth();
  const { showFeedback } = useFeedback();

  /* ===============================
     Estados principales
  =============================== */

  const [bitacora, setBitacora] = useState([]);
  const [loading, setLoading] = useState(false);

  const [enrolados, setEnrolados] = useState([]);
  const [rutSeleccionado, setRutSeleccionado] = useState("");

  /* Async Job */
  const [jobId, setJobId] = useState(null);

  /* Estados para paginación desde el servidor */
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);

  /* ===============================
     Validación Rut enrolado
  =============================== */

  const rutEnrValido = useMemo(() => {
    if (!rutSeleccionado) return false;
    return validarRut(rutSeleccionado);
  }, [rutSeleccionado]);

  /* ===============================
     Cargar enrolados asociados
  =============================== */

  useEffect(() => {
    if (!user?.usuario) return;

    (async () => {
      try {
        const RutCli = normalizeRut(user.usuario);

        const { data } = await api.post("/enrolados/enroladosasociados", {
          RutCli,
          RutEnr: "",
          Tipousr: 1,
        });

        if (data?.success && data?.rows) {
          setEnrolados(data.rows);
        } else {
          setEnrolados([]);

          showFeedback({
            message: "No se encontraron enrolados asociados",
            severity: "warning",
          });
        }
      } catch {
        showFeedback({
          message: "No fue posible cargar enrolados",
          severity: "error",
        });
      }
    })();
  }, [user?.usuario, showFeedback]);

  /* ===============================
     Bitácora server-side
  =============================== */

  const fetchBitacora = useCallback(async () => {
    if (!user?.usuario) return;

    setLoading(true);

    try {
      const RutCli = normalizeRut(user.usuario);

      const { data } = await api.post("/rentas/getbitacora", {
        RutCli,
        RutEnr: "",
        Tipousr: 1, /* cliente */
        page,
        pageSize,
      });

      if (data?.success) {
        setBitacora(data.rows || []);
        setRowCount(data.total || 0);
      } else {
        setBitacora([]);
        setRowCount(0);
      }
    } catch {
      setBitacora([]);
      setRowCount(0);

      showFeedback({
        message: "No fue posible obtener la bitácora",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [user?.usuario, page, pageSize, showFeedback]);

  /* llamar a cargar bitacora */
  useEffect(() => {
    fetchBitacora();
  }, [fetchBitacora]);

  /* ===============================
     Polling Async Job Status
  =============================== */

  useEffect(() => {
    if (!jobId) return;

    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(
          `/extraerf22renta/status/${jobId}`
        );

        if (data?.state === "completed") {
          clearInterval(interval);

          setJobId(null);

          showFeedback({
            message: "✅ Extracción finalizada correctamente",
            severity: "success",
          });

          fetchBitacora();
        }

        if (data?.state === "failed") {
          clearInterval(interval);

          setJobId(null);

          showFeedback({
            message: "❌ Falló el proceso. Intente nuevamente",
            severity: "error",
          });
        }
      } catch {
        clearInterval(interval);

        setJobId(null);

        showFeedback({
          message: "Error al consultar estado del proceso",
          severity: "error",
        });
      }
    }, 2500);

    return () => clearInterval(interval);
  }, [jobId, fetchBitacora, showFeedback]);

  /* ===============================
     Solicitar extracción async
  =============================== */

  const solicitarExtraccion = async () => {
    if (!rutSeleccionado) {
      showFeedback({
        message: "Debe seleccionar un enrolado",
        severity: "warning",
      });
      return;
    }

    if (!rutEnrValido) {
      showFeedback({
        message: "El Rut del enrolado no es válido",
        severity: "error",
      });
      return;
    }

    try {
      const RutCli = normalizeRut(user.usuario);
      const RutEnr = normalizeRut(rutSeleccionado);

      const { data } = await api.post("/extraerf22renta/start", {
        RutCli,
        RutEnr,
        Tipousr: 2, /* Enrolado */
      });

      if (data?.success) {
        setJobId(data.jobId);

        showFeedback({
          message: "📌 Solicitud iniciada en segundo plano",
          severity: "info",
        });
      } else {
        showFeedback({
          message: data?.message || "No fue posible iniciar extracción",
          severity: "error",
        });
      }
    } catch {
      showFeedback({
        message: "Error al iniciar extracción",
        severity: "error",
      });
    }
  };

  /* ===============================
     DataGrid Config
  =============================== */

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "Id",
        width: 90,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "fecha",
        headerName: "Fecha",
        width: 180,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value }) => (
          <Box sx={{ fontSize: "0.75rem", width: "100%", textAlign: "center" }}>
            {formatFecha(value)}
          </Box>
        ),
      },
      {
        field: "tipo",
        headerName: "Tipo",
        width: 120,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "rutcli",
        headerName: "Rut Cliente",
        width: 160,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "rutenr",
        headerName: "Rut Enrolado",
        width: 160,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "detalle",
        headerName: "Detalle",
        width: 200,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "nombre",
        headerName: "Razón Social",
        flex: 1,
        minWidth: 280,
        align: "left",
        headerAlign: "center",
      },
    ],
    []
  );

  /* ===============================
     Render
  =============================== */

  return (
    <Box sx={{ width: "100%", overflow: "visible" }}>
      {/* Barra controles */}
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <TextField
          label="Rut Cliente"
          value={user?.usuario || ""}
          size="small"
          disabled
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{
            minWidth: 150,
            "& .MuiInputBase-root": {
              backgroundColor: (t) => t.custom.ui.readonlyBg,
            },
          }}
        />

        <TextField
          label="Cliente"
          value={user?.nombre || ""}
          size="small"
          disabled
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{
            minWidth: 260,
            flexGrow: 1,
            "& .MuiInputBase-root": {
              backgroundColor: (t) => t.custom.ui.readonlyBg,
            },
          }}
        />

        <TextField
          select
          label="Enrolado"
          value={rutSeleccionado}
          size="small"
          onChange={(e) => setRutSeleccionado(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{
            minWidth: 260,
            flexGrow: 1,
            "& .MuiInputBase-root": {
              backgroundColor: (t) => t.custom.ui.readonlyBg,
            },
          }}
        >
          {enrolados.length > 0 ? (
            enrolados.map((item) => (
              <MenuItem key={item.rut_enr} value={item.rut_enr}>
                {item.nombre_enr || item.rut_enr}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No hay registros</MenuItem>
          )}
        </TextField>

        <Button
          variant="contained"
          startIcon={<RiDownload2Fill size={18} />}
          onClick={solicitarExtraccion}
          sx={{
            minWidth: 130,
            height: 40,
            backgroundColor: (t) => t.custom.ui.primaryAction,
            "&:hover": {
              backgroundColor: (t) => t.custom.ui.primaryActionHover,
            },
          }}
        >
          Solicitar
        </Button>
      </Stack>

      {/* DataGrid */}
      <Box sx={{ height: "calc(100vh - 220px)", minHeight: 0 }}>
        <DataGrid
          rows={bitacora}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          density="compact"
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={({ page, pageSize }) => {
            setPage(page);
            setPageSize(pageSize);
          }}
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 400 },
            },
          }}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          sx={{
            border: "none",
            "& .MuiDataGrid-cell:focus": { outline: "none" },
          }}
        />
      </Box>

      <Typography variant="caption" color="text.secondary">
        {rowCount.toLocaleString("es-CL")} registros encontrados
      </Typography>

    </Box>
  );
};

export default Extraerf22rentaenr;
